/* Botanical Services — "Blossoms". Rounded pill cards in a 3-col
   grid with slight tilt, sage gradient backgrounds, leaf flourishes. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const TILTS = ["", "-rotate-1", "rotate-1"];

function priceFrom(svc) {
  const tiers = Array.isArray(svc?.tiers) ? svc.tiers : [];
  const tp = tiers.map(t => Number(t?.price)).filter(n => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(svc?.price_from ?? svc?.starting_price ?? svc?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function BotanicalServices() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState(null);
  useEffect(() => {
    api.get("/services").then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => setServices([]));
    document.title = `${studioName} — Blossoms`;
  }, [studioName]);

  if (services === null) return <EditorialSkeleton eyebrow="Watering the blossoms." />;

  return (
    <div data-testid="botanical-services" style={{ background: "var(--tenant-bg, #F6F4EE)", color: "var(--tenant-fg, #2A3A20)" }}>

      <section className="px-6 lg:px-12 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-3 mb-5">
          <Leaf className="w-5 h-5" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            Blossoms in Season
          </div>
          <Leaf className="w-5 h-5 -scale-x-100" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
        </div>
        <h1 style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }}>
          <em className="italic">Sessions, by bloom.</em>
        </h1>
      </section>

      <div className="max-w-[1280px] mx-auto px-6 lg:px-12 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((svc, i) => {
            const p = priceFrom(svc);
            return (
              <article key={svc.id} id={svc.id || svc.slug}
                className={`p-8 rounded-[36px] transform ${TILTS[i % TILTS.length]} transition-transform hover:rotate-0 hover:scale-[1.02]`}
                style={{ background: "color-mix(in srgb, var(--tenant-accent) 12%, var(--tenant-bg))" }}>
                <div className="text-[10px] uppercase tracking-[0.42em] mb-4" style={{ color: "var(--tenant-accent)" }}>
                  ❀ Bloom № {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="text-3xl mb-4" style={{ fontFamily: "var(--tenant-serif)" }}>
                  <em className="italic">{svc.name}</em>
                </h2>
                {(svc.description || svc.short_description) && (
                  <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--tenant-fg-soft)" }}>{svc.description || svc.short_description}</p>
                )}
                <div className="flex items-center justify-between">
                  {p !== null && (
                    <div className="text-base italic" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
                      from ${p.toFixed(0)}
                    </div>
                  )}
                  <Link to={tenantHref("/booking")}
                    className="inline-block text-[10px] uppercase tracking-[0.32em] px-5 py-2.5 rounded-full"
                    style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                    Pluck this →
                  </Link>
                </div>
              </article>
            );
          })}
          {services.length === 0 && (
            <p className="col-span-3 text-center italic py-12" style={{ color: "var(--tenant-fg-soft)" }}>
              The garden is between seasons.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
